/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { cloneDeep, flatMap, isEmpty } from 'lodash';
import { createOptions, getYears, monthsPlaceholder } from '../../../Components/helper';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import {
  clearReducer,
  clearReducerTableDetails,
  fetchScopedSuppliers,
  fetchTabColumnDataforPCP,
  getAllProductcpTabs,
  getIndividualDetails,
  getTabColumns,
} from '../store/ProductCP.action';
import { lastOpened } from '../../OprationalCP/store/OprationalCP.action';
import {
  fetchProductIdentifier,
  fetchProductIdentifierType,
  getBrandDetails,
  getCategory1Details,
  getCategory2Details,
  getCustomAttributeDetail,
  getCustomAttributeFreeText,
  getLicensorDetails,
  getPCTypesDetails,
  getProductStatusDetails,
  getSeasonsDetails,
} from '../../SystemMasters/Store/SystemMasters.action';
import { getAdminUser, getCompanyUser } from '../../DevlopmentOrder/store/DevelopmentOrder.action';
import { fetchBomReferenceSupplier } from '../../Library/BomLibrary/store/BomLibrary.action';
import { fetchProductGroup } from '../../RangePlan/ProductList/store/ProductList.action';
import { fetchUserGroupsCards } from '../../UserSetting/store/User.action';

export const StateContext = createContext(null);

/**
 * Reduces a stored answer to the form used when checking whether colourways hold the same value. A
 * checkbox answer is merged into whatever the row held before, so two colourways showing the same
 * ticked options can still carry different keys (one may retain an option explicitly switched off).
 * Comparing only the ticked options treats those as equal, as the user sees them.
 * @param {*} columnValue stored value for one row
 * @returns {string} comparable form of the value
 */
const toComparableAnswer = (columnValue) => {
  if (columnValue == null) return '[]';
  const isOptionMap = typeof columnValue === 'object' && !Array.isArray(columnValue) && !(columnValue instanceof Date);
  if (!isOptionMap) return JSON.stringify(columnValue);
  return JSON.stringify(
    Object.entries(columnValue)
      .filter(([, isSelected]) => isSelected === true)
      .map(([optionIndex]) => optionIndex)
      .sort(),
  );
};

/**
 * Recovers the value a product-level row held for one manual column. That row is display only and
 * is never saved, so it comes back empty after a reload and has to be read back off the colourways
 * it last set. Colourways still holding what the product row put there are read first, so one that
 * was since edited on its own does not drag the product row with it; when none carry that marking,
 * which is how data saved before this existed looks, the colourways are read as a whole. Either way
 * the value is only adopted while every colourway in the set agrees.
 *
 * Callers must pass every colourway row the product has, including the ones still unanswered, so a
 * single answered colourway reads as a disagreement rather than as consensus. This is resolved once
 * and stored on the product row — re-deriving it per render would let a later colourway edit reach
 * back up and empty the product row.
 * @param {object[]} childRows the product's colourway rows for one column, answered or not
 * @returns {*} the value shared by every colourway, or null when any of them differs
 */
export const resolveParentRowValue = (childRows) => {
  const rowsSetFromParent = (childRows || []).filter((childRow) => childRow?.fromParent === true);
  const rowsToRead = rowsSetFromParent.length > 0 ? rowsSetFromParent : childRows || [];
  const childValues = rowsToRead.map((childRow) => childRow?.columnValue ?? null);
  if (childValues.length === 0) return null;
  const [firstValue] = childValues;
  const allAgree = childValues.every((childValue) => toComparableAnswer(childValue) === toComparableAnswer(firstValue));
  if (!allAgree) return null;
  return childValues.find((childValue) => childValue != null) ?? firstValue;
};

export default function CPDetailStateProvider({ children }) {
  const reducerState = useSelector((redState) => redState);
  const {
    UserDetails: { userInfo, isSupplierLoggedIn },
    Users: { logInRights },
    ProductCPReducer: { productCpTabs, productCpColumnTabs, totalproductCp, individualDetail },
  } = reducerState;

  const { CheckActionPermission } = useComponent();
  const dispatch = useDispatch();
  const { pcpId } = useParams();
  const { t } = useTranslation('common');
  const expandSearchRef = useRef(null);
  const showSupplierRef = useRef(false);
  const loggedInUserId = userInfo?.userId;
  const productAddEditAccess = CheckActionPermission(logInRights, 'criticalPath', 'product', 'addEdit');

  const { timeZone } = window.Intl.DateTimeFormat().resolvedOptions();
  const buildSpecialColumns = (isPOBased) => {
    const defaults = [
      { label: 'Colourway', value: 'colorway' },
      { label: 'Factory', value: 'factory' },
      { label: 'Supplier', value: 'supplier' },
    ];
    if (isPOBased) {
      defaults.push(
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SKU_NUMBER'), value: 'skuNumber' },
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.TRANSPORT'), value: 'transport' },
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SIZE'), value: 'size' },
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.LOCATION'), value: 'location' },
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.POQUANTITY'), value: 'quantity' },
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.POAPPROVEDBYDATE'), value: 'approvedByDate' },
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.EXFACTORYDATE'), value: 'exFactoryDate' },
        { label: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SHIPMENTDATE'), value: 'shipmentDate' },
      );
    }
    return defaults;
  };
  const parseStoredArray = (key) => {
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  };
  const initialFilterSetting = {
    brandOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    productTypeOptions: [],
    productStatusOptions: [],
    seasonOptions: [],
    supplierOptions: [],
    createdByOption: [],
    monthOptions: createOptions(monthsPlaceholder, 'name', '_id'),
    yearOptions: createOptions(getYears(), 'name', '_id'),
    activeBrand: [],
    activeProductType: [],
    activeCat1: [],
    activeCat2: [],
    activeCat3: [],
    activeCat4: [],
    activeSupplier: [],
    activeSeason: [],
    activeMonth: [],
    activeYear: [],
    activeStatus: [],
    activeCreatedBy: [],
    dynamicFields: [],
    dynamicTypes: [],
    textCustomAttribute: [],
    productIdentifiers: [],
    columnFilterData: [],
    selectedColumnShow: [],
    activeLicensor: [],
    activeLicense: [],
    activeLikeness: [],
    count: 0,
  };
  const getStoredFilters = () => {
    const storedFilters = localStorage.getItem(`pcpFilters_${pcpId}`);
    if (storedFilters) {
      const parsedFilters = JSON.parse(storedFilters);
      return {
        ...initialFilterSetting,
        activeBrand: parsedFilters.brandId || [],
        activeSubBrand: parsedFilters.subBrandId || [],
        activeProductType: parsedFilters.productType || [],
        activeCat1: parsedFilters.catOneId || [],
        activeCat2: parsedFilters.catTwoId || [],
        activeCat3: parsedFilters.catThreeId || [],
        activeCat4: parsedFilters.catFourId || [],
        activeSupplier: parsedFilters.supplierId || [],
        activeSeason: parsedFilters.seasonId || [],
        activeMonth: parsedFilters.month || [],
        activeYear: parsedFilters.year || [],
        activeStatus: parsedFilters.status || [],
        activeCreatedBy: parsedFilters.createdBy || [],
        dynamicTypes: parsedFilters.dynamicTypes || [],
        textCustomAttribute: parsedFilters.textCustomAttribute || [],
        productIdentifiers: parsedFilters.productIdentifiers || [],
        selectedColumnShow: parsedFilters.selectedColumnShow || [],
        activeLicensor: parsedFilters.licensorId || [],
        activeLicense: parsedFilters.licenseId || [],
        activeLikeness: parsedFilters.likenessId || [],
      };
    }
    return initialFilterSetting;
  };

  const [filterState, setFilterState] = useState(localStorage.getItem(`pcpFilters_${pcpId}`) ? { ...getStoredFilters() } : initialFilterSetting);
  const [detailState, setDetailState] = useState({
    searchText: '',
    searchLoaded: false,
    showMobileSearch: false,
    loading: false,
    tableLoading: false,
    hasMore: false,
    pageIndex: 1,
    pageSize: 20,
    filterData: localStorage.getItem(`pcpFilters_${pcpId}`) ? JSON.parse(localStorage.getItem(`pcpFilters_${pcpId}`)) : initialFilterSetting,
    filterOptions: [],
    percentageColumns: [],
    tabId: null,
    tabChanged: false,
    expandAllColumns: false,
    selectAllColumns: false,
    selectedColumns: [],
    selectedColumnShow: [],
    isAssigneChecked: false,
    individualDetailData: {},
    isMainLoading: false,
    columnsTab: [],
    tableColumData: [],
    tableProductDetail: [],
    pinnedColum: [],
    tabs: [],
    isPOBased: false,
    finalPercentageCalculation: [],
    finalCalculaions: [],
    storedChangedData: [],
    columnFilterData: [],
    columnFilterApplied: false,
    columnFetchParams: null,
    LoaderPercentage: 0,
    selectedPinnedColumns: [],
    sortData: {
      sortAt: 'ProductInfo',
      sortBy: null,
      sortOrder: null,
    },
    dependentFilter: {},
  });
  const [columnsFilterData, setColumnsFilterData] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);

  const [expandedYears, setExpandedYears] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState({});
  const keys = [
    'brandId',
    'subBrandId',
    'seasonId',
    'productType',
    'catOneId',
    'catTwoId',
    'catThreeId',
    'catFourId',
    'supplierId',
    'month',
    'year',
    'status',
    'createdBy',
    'dynamicTypes',
    'textCustomAttribute',
    'productIdentifiers',
    'colorwayId',
    'licenseId',
    'licensorId',
    'likenessId',
  ];

  useEffect(() => {
    if (detailState?.searchLoaded) {
      const fetchData = async () => {
        setDetailState((prev) => ({
          ...prev,
          loading: true,
          pageIndex: 1,
          tableColumData: [],
          tableProductDetail: [],
          hasMore: true,
          selectedColumns: [],
        }));
        dispatch(clearReducer());
        let params = {
          productCPId: pcpId,
          tabId: detailState?.tabId || productCpTabs?.[0]?._id,
          pageSize: detailState.pageSize,
          searchText: detailState?.searchText?.trim(),
          pageIndex: 1,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          recalculate: true,
        };
        if (detailState?.filterData && keys.some((key) => Array.isArray(detailState?.filterData?.[key]) && detailState?.filterData?.[key]?.length > 0)) {
          params = {
            ...params,
            ...Object.keys(detailState?.filterData || {}).reduce((acc, key) => {
              if (key !== 'selectedColumnShow' && Array.isArray(detailState.filterData[key]) && detailState.filterData[key].length > 0) {
                acc[key] = detailState.filterData[key];
              }
              return acc;
            }, {}),
          };
        }
        const priorityColumns = ['colorway', 'supplier', 'factory'];
        if (detailState?.isPOBased) {
          priorityColumns.push('skuNumber', 'transport', 'size', 'location', 'quantity', 'approvedByDate', 'exFactoryDate', 'shipmentDate');
        }
        const filterData = detailState?.selectedPinnedColumns?.filter((colId) => !priorityColumns.includes(colId));
        if (filterData?.length > 0) {
          params.allPin = filterData;
        }
        const res = await Promise.all([dispatch(fetchTabColumnDataforPCP(params))]);
        if (res) {
          localStorage.setItem(`pcpSearch_${pcpId}`, detailState?.searchText?.trim());
          setDetailState((prev) => ({
            ...prev,
            loading: false,
            searchLoaded: false,
          }));
        }
      };

      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailState.searchLoaded, dispatch]);

  const filterApis = async () => {
    await Promise.allSettled([
      dispatch(fetchProductIdentifier(t)),
      dispatch(fetchProductIdentifierType(t)),
      dispatch(fetchProductGroup(t)),
      dispatch(
        getBrandDetails({
          loggedInUserId: userInfo?.userId,
          isActive: 1,
        }),
      ),
      dispatch(
        getSeasonsDetails({
          loggedInUserId: userInfo?.userId,
        }),
      ),
      dispatch(
        getCategory1Details({
          categoryType: 1,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      ),
      dispatch(
        getCategory2Details({
          categoryType: 2,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      ),
      dispatch(
        getPCTypesDetails({
          loggedInUserId: userInfo?.userId,
          isActive: true,
          tier: 'Tier1',
          scopeToUser: true,
        }),
      ),
      dispatch(
        getProductStatusDetails({
          loggedInUserId: userInfo?.userId,
          isActive: 1,
        }),
      ),
      dispatch(getCustomAttributeDetail({ isActive: true })),
      dispatch(fetchBomReferenceSupplier({ alphabaticallySorted: true })),
      dispatch(getAdminUser()),
      dispatch(getCompanyUser({ CompanyUsers: true })),
      dispatch(
        getLicensorDetails({
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      ),
    ]);
  };

  const handleIconClick = () => {
    expandSearchRef.current.classList.toggle('active-input');
  };
  const detailInitial = async () => {
    if (localStorage.getItem(`pcpSearch_${pcpId}`)) {
      setDetailState((oldData) => ({
        ...oldData,
        isMainLoading: true,
        searchText: localStorage.getItem(`pcpSearch_${pcpId}`),
      }));
      expandSearchRef.current.classList.toggle('active-input');
    } else {
      setDetailState((oldData) => ({
        ...oldData,
        isMainLoading: false,
      }));
    }
    const lastOpenedParams = {
      modalId: pcpId,
      userId: userInfo?.userId,
      type: 'productCP',
    };
    await Promise.allSettled([
      dispatch(fetchScopedSuppliers()),
      dispatch(lastOpened(lastOpenedParams)),
      dispatch(getIndividualDetails(pcpId)),
      dispatch(fetchUserGroupsCards({ fromPCP: pcpId })),
    ]);
    await filterApis();
  };

  useEffect(() => {
    if (pcpId && userInfo?.userId) {
      detailInitial();
      dispatch(getCustomAttributeFreeText({ isActive: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pcpId, userInfo?.userId]);

  const getTabColumn = async (filters) => {
    if (pcpId && !isEmpty(productCpTabs)) {
      const hasColumnIdFilter = filters?.columnId;
      const hasColumnFilters = filters?.columnFilters && Object.keys(filters.columnFilters).length > 0;
      const pageSize = detailState?.pageSize;
      const pageIndex = hasColumnIdFilter || hasColumnFilters ? 1 : detailState?.pageIndex || 1;
      let params = {
        productCPId: pcpId,
        tabId: detailState?.tabId || productCpTabs?.[0]?._id,
        pageSize,
        searchText: detailState?.searchText,
        pageIndex,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        recalculate: true,
      };
      if (filters && (filters !== 'fromSorting' || !hasColumnIdFilter)) {
        const activeFilters = Object.keys(filters).reduce((acc, key) => {
          if (key === 'resetProductNameFilter') {
            return acc;
          }
          if (
            (key === 'filteredProductIds' && filters.resetProductNameFilter) ||
            key === 'columnFilters' ||
            (Array.isArray(filters[key]) && filters[key].length > 0) ||
            (!Array.isArray(filters[key]) && filters[key] !== null && filters[key] !== undefined)
          ) {
            acc[key] = filters[key];
          }
          return acc;
        }, {});
        if (Object.keys(activeFilters).length > 0 || filters.columnType) {
          params = {
            ...params,
            ...(filters.columnType ? filters : activeFilters),
          };
        }
      }
      if (
        !filters &&
        detailState?.filterData &&
        keys.some((key) => Array.isArray(detailState?.filterData?.[key]) && detailState?.filterData?.[key]?.length > 0)
      ) {
        params = {
          ...params,
          ...Object.keys(detailState?.filterData || {}).reduce((acc, key) => {
            if (key !== 'selectedColumnShow' && Array.isArray(detailState.filterData[key]) && detailState.filterData[key].length > 0) {
              acc[key] = detailState.filterData[key];
            }
            return acc;
          }, {}),
        };
      }
      if (filters && (hasColumnIdFilter || hasColumnFilters || filters === 'fromSorting')) {
        if (filters === 'fromSorting') {
          params = {
            ...params,
            sortData: detailState?.sortData,
          };

          if (detailState?.filterData && keys.some((key) => Array.isArray(detailState?.filterData?.[key]) && detailState?.filterData?.[key]?.length > 0)) {
            params = {
              ...params,
              ...Object.keys(detailState?.filterData || {}).reduce((acc, key) => {
                if (key !== 'selectedColumnShow' && Array.isArray(detailState.filterData[key]) && detailState.filterData[key].length > 0) {
                  acc[key] = detailState.filterData[key];
                }
                return acc;
              }, {}),
            };
          }
        }
        if (!hasColumnFilters && !filters?.onTimeLateFilter && productCpColumnTabs?.allProductIds?.length > 0) {
          params.allProductIds = productCpColumnTabs?.allProductIds;
        }

        const isProductNameFilterReset =
          filters?.resetProductNameFilter ||
          (filters?.columnId === 'productName' && (!filters?.selectedValues || filters?.selectedValues.length === 0) && filters?.filterBy !== 'notfilled') ||
          (filters?.columnFilters?.productName &&
            (!filters.columnFilters.productName.selectedValues || filters.columnFilters.productName.selectedValues.length === 0) &&
            filters.columnFilters.productName.filterBy !== 'notfilled');

        if (isProductNameFilterReset) {
          const hasOtherFilters = filters?.columnFilters && Object.keys(filters.columnFilters).length > 0;
          if (!hasOtherFilters) {
            params.filteredProductIds = [];
          }
        } else if (!hasColumnFilters && productCpColumnTabs?.filteredProductIds?.length > 0) {
          params.filteredProductIds = productCpColumnTabs?.filteredProductIds;
        }
        setDetailState((oldData) => ({
          ...oldData,
          pageIndex,
          tableLoading: true,
          tableColumData: [],
          tableProductDetail: [],
          hasMore: true,
          ...((hasColumnIdFilter || hasColumnFilters) && {
            LoaderPercentage: 0,
            columnFetchParams: params,
          }),
        }));
      } else {
        setDetailState((oldData) => ({
          ...oldData,
          loading: true,
          pageIndex,
          isMainLoading: true,
          hasMore: false,
          tableProductDetail: [],
          tableColumData: [],
          columnFetchParams: null,
          columnFilterApplied: false,
        }));
      }
      const priorityColumns = ['colorway', 'supplier', 'factory'];
      const localStorageIds = localStorage.getItem(`pinnedColumns_${pcpId}`);
      const filData = JSON.parse(localStorageIds);
      let filterData = [];
      if (filData?.length > 0) {
        filterData = filData?.filter((id) => !priorityColumns.includes(id));
      }

      const tabParms = {
        tabId: detailState?.tabId || productCpTabs?.[0]?._id,
        pcpId,
        allPin: filterData || [],
      };
      if (filterData?.length > 0) {
        params.allPin = filterData;
      }
      try {
        if (filters && (hasColumnIdFilter || filters === 'fromSorting')) {
          await dispatch(clearReducerTableDetails());
          const res = await dispatch(fetchTabColumnDataforPCP(params));
          if (res?.code === 200 && res?.data?.productDetails?.length === 0) {
            setDetailState((oldData) => ({
              ...oldData,
              tableLoading: false,
            }));
          }
        } else {
          await dispatch(clearReducer());
          await dispatch(clearReducerTableDetails());
          await Promise.all([dispatch(getTabColumns(tabParms)), dispatch(fetchTabColumnDataforPCP(params))]);
        }
        setDetailState((oldData) => ({
          ...oldData,
          loading: false,
          isMainLoading: false,
          tableLoading: false,
          LoaderPercentage: 100,
        }));
      } catch (error) {
        console.error('Error in getTabColumn:', error);
        setDetailState((oldData) => ({
          ...oldData,
          loading: false,
          isMainLoading: false,
          tableLoading: false,
          LoaderPercentage: 100,
        }));
      }
    }
  };

  useEffect(() => {
    if (pcpId && detailState?.tableColumData?.length > 0 && detailState?.sortData?.sortBy) {
      getTabColumn('fromSorting');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pcpId, JSON.stringify(detailState?.sortData)]);

  useEffect(() => {
    const columDetails = async () => {
      if (pcpId) {
        setDetailState((oldData) => ({
          ...oldData,
          loading: true,
        }));
        await dispatch(getAllProductcpTabs({ productCPId: pcpId, fromDetails: true }));
        setDetailState((oldData) => ({
          ...oldData,
          loading: false,
        }));
      }
    };
    columDetails();
  }, [dispatch, pcpId]);

  useEffect(() => {
    const fetchAllPages = async () => {
      if (!detailState?.columnFilterApplied || !totalproductCp || !detailState?.columnFetchParams) return;

      const { pageSize, columnFetchParams } = detailState;
      const fetchParams = { ...columnFetchParams, recalculate: false };
      const priorityColumns = ['colorway', 'supplier', 'factory'];
      const filterData = detailState?.selectedPinnedColumns?.filter((colId) => !priorityColumns.includes(colId));
      if (filterData?.length > 0) {
        fetchParams.allPin = filterData;
      }
      try {
        const totalPages = Math.ceil(Number(totalproductCp) / Number(pageSize));
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        if (remainingPages?.length > 0) {
          for (let index = 0; index < remainingPages.length; index++) {
            const nextPage = remainingPages[index];
            try {
              await dispatch(
                fetchTabColumnDataforPCP({
                  ...fetchParams,
                  pageIndex: nextPage,
                }),
              );
              const progress = Math.round(((index + 2) / totalPages) * 100);
              setDetailState((prev) => ({
                ...prev,
                LoaderPercentage: Math.min(progress, 100),
              }));
              if (index === remainingPages.length - 1) {
                setDetailState((prev) => ({
                  ...prev,
                  loading: false,
                  isMainLoading: false,
                  columnFilterApplied: false,
                  tableLoading: false,
                  hasMore: false,
                  LoaderPercentage: 100,
                }));
              }
            } catch (error) {
              console.error(`Error fetching page ${nextPage}:`, error);
              setDetailState((prev) => ({
                ...prev,
                loading: false,
                tableLoading: false,
                isMainLoading: false,
                hasMore: false,
                LoaderPercentage: 100,
              }));
              break;
            }
          }
        } else {
          setDetailState((prev) => ({
            ...prev,
            loading: false,
            isMainLoading: false,
            columnFilterApplied: false,
            tableLoading: false,
            hasMore: false,
            LoaderPercentage: 100,
          }));
        }
      } catch (error) {
        console.error('Error in fetchAllPages:', error);
        setDetailState((prev) => ({ ...prev, loading: false, isMainLoading: false, tableLoading: false, LoaderPercentage: 100 }));
      }
    };
    fetchAllPages();
  }, [detailState?.columnFilterApplied, totalproductCp, detailState?.columnFetchParams, detailState?.pageSize, dispatch]);

  useEffect(() => {
    if (detailState?.tabChanged) {
      return;
    }
    if (pcpId && localStorage.getItem(`pcpFilters_${pcpId}`)) {
      const filters = localStorage.getItem(`pcpFilters_${pcpId}`);
      delete filters.selectedColumnShow;
      getTabColumn(JSON.parse(localStorage.getItem(`pcpFilters_${pcpId}`)));
    } else {
      getTabColumn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pcpId, productCpTabs]);

  useEffect(() => {
    if (productCpTabs && !isEmpty(productCpTabs)) {
      setDetailState((oldState) => {
        const selectedTabId = cloneDeep(oldState.tabId);
        return {
          ...oldState,
          tabs: productCpTabs,
          tabId: selectedTabId || productCpTabs?.[0]?._id,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCpTabs]);

  const handlePinnedColumnsChange = async () => {
    if (pcpId && detailState?.selectedPinnedColumns !== undefined && detailState?.columnsTab?.length > 0 && detailState?.selectedPinnedColumns?.length > 0) {
      setDetailState((prev) => ({
        ...prev,
        loading: true,
        pageIndex: 1,
        tableColumData: [],
        tableProductDetail: [],
        hasMore: true,
        selectedColumns: [],
      }));

      await dispatch(clearReducerTableDetails());

      // if (detailState?.filterData && keys.some((key) => Array.isArray(detailState?.filterData?.[key]) && detailState?.filterData?.[key]?.length > 0)) {
      const activeColumnFilters = columnsFilterData?.filter((item) => item.filterApplied);
      if (activeColumnFilters?.length > 0 && detailState?.columnFetchParams) {
        await getTabColumn(detailState.columnFetchParams);
      } else if (detailState?.filterData && keys.some((key) => Array.isArray(detailState?.filterData?.[key]) && detailState?.filterData?.[key]?.length > 0)) {
        await getTabColumn(detailState.filterData);
      } else {
        await getTabColumn();
      }
      // }
    }
  };
  useEffect(() => {
    const { columnsTab, tabs } = detailState;
    if (columnsTab?.length > 0 && filterState?.count === 0) {
      const activeColumns = (tabs || [])
        ?.filter((tab) => tab?.columns?.length > 0)
        ?.map((tab) => tab?.columns?.filter((col) => col?.isActive && !col?.isDeleted))
        ?.filter(Boolean);
      const optionData = flatMap(activeColumns).map((col) => ({
        label: col?.name,
        value: col?._id,
        groupName: tabs.find((tabN) => tabN._id === col.tabId)?.name,
      }));
      if (isSupplierLoggedIn && !showSupplierRef.current) {
        showSupplierRef.current = columnsTab.some((col) => col.assignee === loggedInUserId);
      }
      const storedSelected = parseStoredArray(`selectedColumns_${pcpId}`);
      const defaultColumn = buildSpecialColumns(detailState?.isPOBased);
      const defaultSelectedIds =
        storedSelected?.length > 0 ? storedSelected : [...defaultColumn.map((col) => col.value), ...optionData.map((col) => col.value)];
      setDetailState((prev) => ({
        ...prev,
        selectedColumnShow: defaultSelectedIds,
        columnFilterData: [...defaultColumn, ...optionData],
        tabId: tabs?.[0]?._id,
      }));
      if (filterState?.count === 0) {
        let filterObject = {};
        if (localStorage.getItem(`pcpFilters_${pcpId}`)) {
          filterObject = JSON.parse(localStorage.getItem(`pcpFilters_${pcpId}`));
        }
        setFilterState((prev) => ({
          ...prev,
          ...filterObject,
          count: 1,
        }));
      }
    }
  }, [detailState?.tabs?.length, detailState?.columnsTab?.length, isSupplierLoggedIn, showSupplierRef.current, loggedInUserId]);

  useEffect(() => {
    if (!productCpColumnTabs) return;

    const combinationData = productCpColumnTabs?.productDetails || [];
    const isPOBased = individualDetail?.basedOnPO === true;
    const colTab = detailState?.columnsTab || [];
    const manualColumns = colTab.filter((data) => data?.columnType === 'manual');
    const answersData = productCpColumnTabs?.answers || [];

    const getMatchKey = (item) => {
      if (isPOBased) {
        return `${item?.rowKey}_${item?.columnId}`;
      }
      return `${item?.productId}_${item?.columnId}_${item?.productCPId}_${item?.tabId}_${item?.supplierId || 'null'}_${item?.colourId || 'null'}_${
        item?.factoryId || 'null'
      }`;
    };

    const hasData = (item) => item?.columnValue != null || item?.estimatedDate != null;

    const existingAnswerKeys = new Set([...(detailState?.tableColumData || []).map((ans) => getMatchKey(ans)), ...answersData.map((ans) => getMatchKey(ans))]);
    const knownRowsByMatchKey = new Map();
    [...(detailState?.tableColumData || []), ...answersData].forEach((row) => {
      const rowKeyForMatch = getMatchKey(row);
      if (!knownRowsByMatchKey.has(rowKeyForMatch)) knownRowsByMatchKey.set(rowKeyForMatch, row);
    });

    const newManualEntries = [];

    combinationData.forEach((item) => {
      if (isPOBased) {
        const allRows = [item, ...(item?.expandData || [])];

        allRows.forEach((row) => {
          manualColumns.forEach((colData) => {
            const potentialKey = `${row?.rowKey}_${colData?._id}`;
            if (!existingAnswerKeys.has(potentialKey)) {
              newManualEntries.push({
                rowKey: row?.rowKey,
                productId: row?.productId,
                poId: item?.poId,
                poOrderDetailIndex: row?.poOrderDetailIndex,
                supplierId: row?.supplierId || null,
                factoryId: row?.factoryId || null,
                colourId: row?.colourId || null,
                sizeId: row?.sizeId || null,
                skuNumber: row?.skuNumber || null,
                location: row?.location || null,
                transport: row?.transport || null,
                quantity: row?.quantity || null,
                approvedByDate: row?.approvedByDate || null,
                exFactoryDate: row?.exFactoryDate || null,
                shipmentDate: row?.shipmentDate || null,
                estimatedDate: null,
                columnValue: null,
                columnId: colData?._id,
                tabId: colData?.tabId,
                productCPId: colData?.productCPId,
                type: colData?.manualType?.key || colData?.columnType,
              });
            }
          });
        });
      } else if (item?.expandData?.length > 0) {
        const childRowsByColumnId = new Map();
        item.expandData.forEach((ele) => {
          manualColumns.forEach((colData) => {
            const supplierId = ele?.supplierId || 'null';
            const colourId = ele?.colourId || 'null';
            const factoryId = ele?.factoryId || 'null';
            const potentialKey = `${item?._id}_${colData?._id}_${colData?.productCPId}_${colData?.tabId}_${supplierId}_${colourId}_${factoryId}`;
            let childRow = knownRowsByMatchKey.get(potentialKey);
            if (!childRow) {
              childRow = {
                productId: item?._id,
                colourId: ele?.colourId || null,
                supplierId: ele?.supplierId || null,
                factoryId: ele?.factoryId || null,
                estimatedDate: null,
                columnValue: null,
                columnId: colData?._id,
                tabId: colData?.tabId,
                productCPId: colData?.productCPId,
                type: colData?.manualType?.key || colData?.columnType,
                location: ele?.location || null,
                transport: ele?.transport || null,
                size: ele?.size || null,
                skuNumber: ele?.skuNumber || null,
                quantity: ele?.quantity || null,
                approvedByDate: ele?.approvedByDate || null,
                exFactoryDate: ele?.exFactoryDate || null,
                shipmentDate: ele?.shipmentDate || null,
              };
            }
            if (!existingAnswerKeys.has(potentialKey)) {
              newManualEntries.push(childRow);
            }
            const columnChildRows = childRowsByColumnId.get(colData?._id) || [];
            columnChildRows.push(childRow);
            childRowsByColumnId.set(colData?._id, columnChildRows);
          });
        });
        manualColumns.forEach((colData) => {
          const parentKey = `${item?._id}_${colData?._id}_${colData?.productCPId}_${colData?.tabId}_null_null_null`;
          if (!existingAnswerKeys.has(parentKey)) {
            newManualEntries.push({
              productId: item?._id,
              colourId: null,
              supplierId: null,
              factoryId: null,
              estimatedDate: resolveParentRowPlannedDate(childRowsByColumnId.get(colData?._id)),
              columnId: colData?._id,
              tabId: colData?.tabId,
              productCPId: colData?.productCPId,
              type: colData?.manualType?.key || colData?.columnType,
              isParentRow: true,
              columnValue: resolveParentRowValue(childRowsByColumnId.get(colData?._id)),
            });
          }
        });
      } else {
        manualColumns.forEach((colData) => {
          const potentialKey = `${item?._id}_${colData?._id}_${colData?.productCPId}_${colData?.tabId}_null_null_null`;
          if (!existingAnswerKeys.has(potentialKey)) {
            newManualEntries.push({
              productId: item?._id,
              colourId: null,
              supplierId: null,
              factoryId: null,
              estimatedDate: null,
              columnValue: null,
              columnId: colData?._id,
              tabId: colData?.tabId,
              productCPId: colData?.productCPId,
              type: colData?.manualType?.key || colData?.columnType,
            });
          }
        });
      }
    });

    const mergedData = [...answersData, ...newManualEntries];
    const sourceData = mergedData.filter((item) => item?.columnType === 'source');
    const nonSourceData = mergedData.filter((item) => item?.columnType !== 'source');

    const existingColumnDataMap = new Map();
    detailState?.tableColumData?.forEach((item) => {
      const key = getMatchKey(item);
      existingColumnDataMap.set(key, item);
    });

    nonSourceData.forEach((item) => {
      const key = getMatchKey(item);
      const existing = existingColumnDataMap.get(key);
      if (!existing || (hasData(item) && !hasData(existing))) {
        existingColumnDataMap.set(key, item);
      }
    });

    const existingSourceKeys = new Set((detailState?.tableColumData || []).filter((item) => item?.columnType === 'source').map((item) => getMatchKey(item)));

    const newSourceData = sourceData.filter((item) => !existingSourceKeys.has(getMatchKey(item)));

    const finalUniqueData = [...Array.from(existingColumnDataMap.values()), ...newSourceData];

    const tabColumns = detailState?.tabs?.filter((tab) => tab?.columns?.length > 0).map((tab) => tab?.columns);

    const staticValue = buildSpecialColumns(isPOBased)?.map((col) => ({
      name: col.label,
      _id: col.value,
    }));

    const activeTabColumns = (tabColumns || []).map((cols) => cols?.filter((col) => col?.isActive && !col?.isDeleted));

    const columData = [...staticValue, ...activeTabColumns];

    const optionData = flatMap(columData || [])?.map((col) => ({
      label: col?.name || col?.keyName,
      value: col?._id,
      groupName: detailState?.tabs.find((tabN) => tabN._id === col.tabId)?.name,
    }));

    const storedSelectedColumns = parseStoredArray(`selectedColumns_${pcpId}`);
    const defaultSelectedColumns = storedSelectedColumns?.length > 0 ? storedSelectedColumns : optionData?.map((e) => e.value);

    setDetailState((oldState) => {
      const totalCount = totalproductCp || 0;
      const pageSize = oldState.pageSize || 50;
      const totalPages = Math.ceil(totalCount / pageSize);
      const hasMoreProduct = oldState.pageIndex < totalPages;

      const currentPageData = combinationData;
      let updatedTableProductDetail;

      const hasFullDataset = totalCount > 0 && combinationData?.length > 0 && combinationData.length >= totalCount;
      const isInitialLoad = oldState.pageIndex === 1 || oldState.tableProductDetail.length === 0;

      if (hasFullDataset) {
        updatedTableProductDetail = currentPageData;
      } else if (isInitialLoad) {
        updatedTableProductDetail = currentPageData;
      } else {
        const existingRowKeys = new Set(
          oldState.tableProductDetail.map((item) => {
            if (isPOBased) {
              return item?.rowKey;
            }
            return `${item?._id}_${item?.colourId || 'null'}_${item?.supplierId || 'null'}_${item?.factoryId || 'null'}`;
          }),
        );

        const newItems = currentPageData.filter((item) => {
          if (isPOBased) {
            return !existingRowKeys.has(item?.rowKey);
          }
          const itemKey = `${item?._id}_${item?.colourId || 'null'}_${item?.supplierId || 'null'}_${item?.factoryId || 'null'}`;
          return !existingRowKeys.has(itemKey);
        });

        updatedTableProductDetail = [...oldState.tableProductDetail, ...newItems];
      }

      return {
        ...oldState,
        tableColumData: finalUniqueData,
        tableProductDetail: updatedTableProductDetail,
        finalPercentageCalculation: productCpColumnTabs?.finalPercentageCalculation?.length
          ? productCpColumnTabs.finalPercentageCalculation
          : oldState?.finalPercentageCalculation,
        finalCalculaions: productCpColumnTabs?.finalCalculaions?.length > 0 ? productCpColumnTabs.finalCalculaions : oldState?.finalCalculaions,
        hasMore: hasFullDataset ? false : hasMoreProduct,
        tabChanged: false,
        tableLoading: answersData?.length === 0 || hasFullDataset ? false : oldState.tableLoading,
        LoaderPercentage: (() => {
          if (answersData?.length === 0) return 100;
          if (hasFullDataset) return 100;
          if (hasMoreProduct && oldState?.columnFetchParams) {
            return Math.round((1 / Math.max(1, Math.ceil(Number(totalproductCp) / Number(oldState?.pageSize || 50)))) * 100);
          }
          return oldState.LoaderPercentage;
        })(),
        columnFilterApplied: (() => {
          if (answersData?.length === 0) return false;
          if (hasFullDataset) return false;
          return Boolean(hasMoreProduct && oldState?.columnFetchParams);
        })(),
        columnFilterData: optionData,
        selectedColumnShow: oldState?.selectedColumnShow?.length ? oldState.selectedColumnShow : defaultSelectedColumns,
        isPOBased,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productCpColumnTabs?.columns?.length,
    productCpColumnTabs?.answers,
    productCpColumnTabs?.productDetails?.length,
    productCpColumnTabs?.finalPercentageCalculation,
    productCpColumnTabs?.finalCalculaions,
    totalproductCp,
    detailState?.columnsTab?.length,
  ]);

  const contextValue = useMemo(() => {
    return {
      detailState,
      setDetailState,
      filterState,
      setFilterState,
      timeZone,
      handleIconClick,
      expandSearchRef,
      initialFilterSetting,
      keys,
      showSupplierRef,
      productAddEditAccess,
      getTabColumn,
      columnsFilterData,
      setColumnsFilterData,
      selectedDays,
      setSelectedDays,
      expandedYears,
      setExpandedYears,
      expandedMonths,
      setExpandedMonths,
      handlePinnedColumnsChange,
    };
  }, [
    detailState,
    setDetailState,
    filterState,
    setFilterState,
    timeZone,
    handleIconClick,
    expandSearchRef,
    initialFilterSetting,
    keys,
    showSupplierRef,
    productAddEditAccess,
    getTabColumn,
    JSON.stringify(columnsFilterData),
    setColumnsFilterData,
    selectedDays,
    setSelectedDays,
    expandedYears,
    setExpandedYears,
    expandedMonths,
    setExpandedMonths,
    handlePinnedColumnsChange,
  ]);

  return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
}

export function useCPDetailState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the DAMModule State Provider');
  }
  return context;
}

/**
 * Resolves the planned date the product row shows for one column: the date every combination row
 * shares, or null when they differ — the same agreement rule resolveParentRowValue applies to actuals.
 * @param {object[]} childRows the product's combination rows for one column, planned or not
 * @returns {*} the shared planned date, or null when any row differs
 */
export function resolveParentRowPlannedDate(childRows) {
  const plannedDates = (childRows || []).map((childRow) => childRow?.estimatedDate ?? null);
  if (plannedDates.length === 0) return null;
  const [firstPlanned] = plannedDates;
  const allAgree = plannedDates.every((plannedDate) => toComparableAnswer(plannedDate) === toComparableAnswer(firstPlanned));
  return allAgree ? firstPlanned : null;
}
